import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { formatE164USPhoneNumber, formatPhoneAsYouType, stripPhoneNumber } from 'shared';

interface PhoneInputProps {
    value: string;
    onChange: (e164Value: string, displayValue: string) => void;
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
}

function toDisplayValue(value: string) {
    if (!value) return '';
    const digits = stripPhoneNumber(value);
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return formatPhoneAsYouType(national);
}

export function PhoneInput({
    value,
    onChange,
    id,
    placeholder = '(555) 555-5555',
    disabled,
    fullWidth,
}: PhoneInputProps) {
    const [display, setDisplay] = useState(() => toDisplayValue(value));
    const [lastFormatted, setLastFormatted] = useState('');
    const emitted = useRef({ e164: value, display: toDisplayValue(value) });

    useEffect(() => {
        if (value === emitted.current.e164 || value === emitted.current.display) return;
        const displayVal = toDisplayValue(value);
        emitted.current = { e164: value, display: displayVal };
        setDisplay(displayVal);
    }, [value]);

    function handleChange(raw: string) {
        const digits = stripPhoneNumber(raw);
        const formatted = formatPhoneAsYouType(digits);
        const displayVal = formatted === lastFormatted ? formatted.slice(0, -1) : formatted;
        setLastFormatted(displayVal);
        setDisplay(displayVal);

        const e164 = formatE164USPhoneNumber(digits)?.toString() || digits;
        emitted.current = { e164, display: displayVal };
        onChange(e164, displayVal);
    }

    return (
        <StyledInput
            id={id}
            type="tel"
            value={display}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            fullWidth={fullWidth}
            maxLength={14}
        />
    );
}

const StyledInput = styled('input')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    width: fullWidth ? '100%' : 'auto',
    padding: '10px 14px',
    fontSize: 14,
    color: Colors.primary,
    backgroundColor: Colors.surface,
    border: `1px solid ${Colors.border}`,
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
        borderColor: Colors.textSecondary,
    },
    '&:disabled': {
        backgroundColor: Colors.fill,
        color: Colors.textSecondary,
    },
    '&::placeholder': {
        color: Colors.border,
    },
}));
